import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import configs and util modules
import appSettings from '../../../../config/settings';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { formatMoney } from '../../../../util/currency';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { FIXED, isBookingProcess } from '../../../../transactions/transaction';

// Import shared components
import { Button, Form, FieldCurrencyInput } from '../../../../components';

import BookingPriceVariants from './BookingPriceVariants';
import StartTimeInterval from './StartTimeInverval';

// Import modules from this directory
import css from './EditListingPricingForm.module.css';
import CustomFieldArray from './CustomFieldArray';
import { required, validateInteger } from '../../../../util/validators';
import DurationFieldArray from './DurationFieldArray';

const { Money } = sdkTypes;

const getPriceValidators = (listingMinimumPriceSubUnits, marketplaceCurrency, intl) => {
  const priceRequiredMsgId = { id: 'EditListingPricingForm.priceRequired' };
  const priceRequiredMsg = intl.formatMessage(priceRequiredMsgId);
  const priceRequired = validators.required(priceRequiredMsg);

  const minPriceRaw = new Money(500, marketplaceCurrency);
  const minPrice = formatMoney(intl, minPriceRaw);
  const priceTooLowMsgId = { id: 'EditListingPricingForm.priceTooLow' };
  const priceTooLowMsg = intl.formatMessage(priceTooLowMsgId, { minPrice });
  const minPriceRequired = validators.moneySubUnitAmountAtLeast(
    priceTooLowMsg,
    listingMinimumPriceSubUnits
  );

  return listingMinimumPriceSubUnits
    ? validators.composeValidators(priceRequired, minPriceRequired)
    : priceRequired;
};

const ErrorMessages = props => {
  const { fetchErrors } = props;
  const { updateListingError, showListingsError } = fetchErrors || {};

  return (
    <>
      {updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingPricingForm.updateFailed" />
        </p>
      ) : null}
      {showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingPricingForm.showListingFailed" />
        </p>
      ) : null}
    </>
  );
};

/**
 * The EditListingPricingForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.formId] - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.unitType - The unitType from listing.attributes.publicData
 * @param {Object} [props.listingTypeConfig] - The listing type config that matches with listingType on publicData.
 * @param {Object} [props.listingTypeConfig.priceVariations] - The price variations config.
 * @param {boolean} props.listingTypeConfig.priceVariations.enabled - Whether the price variations are enabled.
 * @param {Object} [props.listingTypeConfig.transactionType] - The transaction type config.
 * @param {string} props.listingTypeConfig.transactionType.process - The transaction process config.
 * @param {string} props.marketplaceCurrency - The marketplace currency
 * @param {number} [props.listingMinimumPriceSubUnits] - The listing minimum price sub units
 * @param {boolean} [props.autoFocus] - Whether the input should be focused
 * @param {boolean} [props.disabled] - Whether the form is disabled
 * @param {boolean} [props.ready] - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {boolean} [props.invalid] - Whether the form is invalid
 * @param {boolean} [props.pristine] - Whether the form is pristine
 * @param {string} props.saveActionMsg - The save action message
 * @param {boolean} [props.updated] - Whether the form is updated
 * @param {boolean} [props.updateInProgress] - Whether the form is updating
 * @param {Object} [props.fetchErrors] - The fetch errors
 * @returns {JSX.Element}
 */
export const EditListingPricingForm = props => (
  <FinalForm
    mutators={{ ...arrayMutators }}
    {...props}
    render={formRenderProps => {
      const {
        formId = 'EditListingPricingForm',
        form: formApi,
        form: {
          mutators: { push, pop },
        },
        autoFocus,
        className,
        rootClassName,
        disabled,
        ready,
        handleSubmit,
        marketplaceCurrency,
        unitType,
        listingTypeConfig,
        isPriceVariationsInUse,
        listingMinimumPriceSubUnits = 0,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress = false,
        fetchErrors,
        initialValues: formInitialValues,
        values: formValues,
        minDuration
      } = formRenderProps;


      const intl = useIntl();
      const priceValidators = getPriceValidators(
        listingMinimumPriceSubUnits,
        marketplaceCurrency,
        intl
      );

      const priceValidatorsForLocations = getPriceValidators(
        0,
        marketplaceCurrency,
        intl
      );

      const descriptionValidators = required(
        intl.formatMessage({
          id: 'EditListingDetailsForm.locationRequired',
        })
      );

      const durationValidators = (value, min, max, intl) => {
        const requiredMsg = intl.formatMessage({ id: 'FieldSeatsInput.seatsError' });
        const numberTooSmallMessage = intl.formatMessage(
          { id: 'FieldSeatsInput.numberTooSmall' },
          { min }
        );
        const numberTooBigMessage = intl.formatMessage({ id: 'FieldSeatsInput.numberTooBig' }, { max });

        return value == null
          ? requiredMsg
          : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
      };

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const { transactionType } = listingTypeConfig || {};
      const { process } = transactionType || {};
      const isBooking = isBookingProcess(process);

      const isFixedLengthBooking = isBooking && unitType === FIXED;
      const isBookingPriceVariationsInUse = isBooking && isPriceVariationsInUse;
      const isUsingPriceVariants = isFixedLengthBooking || isBookingPriceVariationsInUse;

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          <ErrorMessages fetchErrors={fetchErrors} />

          {/*{isUsingPriceVariants ? (*/}
          {/*  <BookingPriceVariants*/}
          {/*    formId={formId}*/}
          {/*    formApi={formApi}*/}
          {/*    autoFocus={autoFocus}*/}
          {/*    className={css.input}*/}
          {/*    marketplaceCurrency={marketplaceCurrency}*/}
          {/*    unitType={unitType}*/}
          {/*    isPriceVariationsInUse={isBookingPriceVariationsInUse}*/}
          {/*    initialLengthOfPriceVariants={formInitialValues?.priceVariants?.length || 0}*/}
          {/*    listingMinimumPriceSubUnits={listingMinimumPriceSubUnits}*/}
          {/*  />*/}
          {/*) : (*/}
          {/*  <FieldCurrencyInput*/}
          {/*    id={`${formId}price`}*/}
          {/*    name="price"*/}
          {/*    className={css.input}*/}
          {/*    autoFocus={autoFocus}*/}
          {/*    label={intl.formatMessage(*/}
          {/*      { id: 'EditListingPricingForm.pricePerProduct' },*/}
          {/*      { unitType }*/}
          {/*    )}*/}
          {/*    placeholder={intl.formatMessage({*/}
          {/*      id: 'EditListingPricingForm.priceInputPlaceholder',*/}
          {/*    })}*/}
          {/*    currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}*/}
          {/*    validate={priceValidators}*/}
          {/*  />*/}
          {/*)}*/}

          <div className={css.sectionHolder}>
            {isFixedLengthBooking ? (
              <StartTimeInterval
                name="startTimeInterval"
                idPrefix={`${formId}_startTimeInterval`}
                formValues={formValues}
                pristine={pristine}
              />
            ) : null}
          </div>

          <div className={css.sectionHolder}>
            <h3 className={css.sectionTitle}>
              {intl.formatMessage({ id: 'EditListingPricingForm.durationTitle' })}
            </h3>
            <div className={css.optionsList}>
              <DurationFieldArray
                push={push}
                pop={pop}
                intl={intl}
                addButtonText={intl.formatMessage({ id: 'EditListingDetailsForm.addButtonText' })}
                removeButtonText={intl.formatMessage({
                  id: 'EditListingDetailsForm.removeButtonText',
                })}
                arrayName={'optionDuration'}
                optionFieldName={'optionDuration'}
                optionFieldLabel={intl.formatMessage({
                  id: 'EditListingDetailsForm.optionFieldLabel',
                })}
                optionFieldPlaceHolder={intl.formatMessage({
                  id: 'EditListingDetailsForm.optionFieldPlaceHolder',
                })}
                priceFieldName={'optionDurationPrice'}
                priceFieldLabel={intl.formatMessage({
                  id: 'EditListingDetailsForm.priceFieldLabel',
                })}
                priceFieldPlaceHolder={intl.formatMessage({
                  id: 'EditListingDetailsForm.priceFieldPlaceHolder',
                })}
                priceValidators={priceValidators}
                descriptionFieldName={'optionDurationDescription'}
                descriptionFieldLabel={intl.formatMessage({
                  id: 'EditListingDetailsForm.descriptionFieldLabel',
                })}
                descriptionFieldPlaceHolder={intl.formatMessage({
                  id: 'EditListingDetailsForm.descriptionFieldPlaceHolder',
                })}
                descriptionValidators={required(intl.formatMessage({
                    id: 'EditListingDetailsForm.descriptionFieldRequired',
                  })
                )}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                minDuration={minDuration}
                durationValidators={durationValidators}
                formValues={formValues}
              />
            </div>
          </div>

          <div className={css.sectionHolder}>
            <h3 className={css.sectionTitle}>
              {intl.formatMessage({ id: 'EditListingPricingForm.optionsTitle' })}
            </h3>

            <div className={css.optionsList}>
              <CustomFieldArray
                push={push}
                pop={pop}
                addButtonText={intl.formatMessage({ id: 'EditListingPricingForm.addButtonText' })}
                removeButtonText={intl.formatMessage({
                  id: 'EditListingPricingForm.removeButtonText',
                })}
                arrayName={'additionalLocations'}
                optionFieldName={'additionalLocations'}
                optionFieldLabel={intl.formatMessage({
                  id: 'EditListingPricingForm.optionFieldLabel',
                })}
                optionFieldPlaceHolder={intl.formatMessage({
                  id: 'EditListingPricingForm.optionFieldPlaceHolder',
                })}
                priceFieldName={'priceRateAddOnPrice'}
                priceFieldLabel={intl.formatMessage({
                  id: 'EditListingPricingForm.priceFieldLabel',
                })}
                priceFieldPlaceHolder={intl.formatMessage({
                  id: 'EditListingPricingForm.priceFieldPlaceHolder',
                })}
                descriptionValidators={descriptionValidators}
                priceValidators={priceValidatorsForLocations}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                formValues={formValues}
              />
            </div>
          </div>

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

export default EditListingPricingForm;
